import { isDevelopmentEnv } from "@eroad/helpers/isDevelopmentEnv";
import { isTestRunEnv } from "@eroad/helpers/isTestRunEnv";

import { captureException } from "../sentry/sentry";

declare global {
  interface Window {
    pendo?: pendo.Pendo;
  }
}

/**
 * Identify the user in Pendo with visitor information.
 * This function should be called after Pendo is initialized.
 *
 * @param config - Pendo Identity object.
 */
function identifyUserInPendo(config: pendo.Identity): void {
  if (!isPendoEnabled()) {
    return;
  }

  try {
    window.pendo?.identify(config);
  } catch (error) {
    if (error instanceof Error) {
      captureException(error, {
        tags: {
          manuallyReported: "true",
        },
      });
    }
  }
}

/**
 * Initialize Pendo before using it.
 * This function should be called once at the start of your application.
 */
function initPendo(): void {
  if (!isPendoEnabled()) {
    return;
  }

  try {
    window.pendo?.initialize({
      account: { id: "" },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error: autoFrameInstall and frameIdentitySync is not yet in the pendo type definitions
      autoFrameInstall: true,
      frameIdentitySync: true,
      visitor: { id: "" },
    });
  } catch (error) {
    if (error instanceof Error) {
      captureException(error, {
        tags: {
          manuallyReported: "true",
        },
      });
    }
  }
}

/**
 * @internal
 */
function isPendoEnabled(): boolean {
  return !isDevelopmentEnv() && !isTestRunEnv();
}

/**
 * Trigger a pendo Track Event.
 * This function should be called after Pendo is initialized.
 *
 * @param action - Tracking name to show up in Track Events UI.
 * @param event - This only shows up in a Visitor Timeline.
 */
function trackPendoEvent(action: string, metadata: Record<string, string[] | boolean | number | string | null>) {
  if (!isPendoEnabled()) {
    return;
  }

  const pendoTrack = window.pendo?.track;

  if (typeof pendoTrack === "function") {
    try {
      pendoTrack(action, metadata);
    } catch (error) {
      if (error instanceof Error) {
        captureException(error, {
          tags: {
            manuallyReported: "true",
          },
        });
      }
    }
  }
}

export { identifyUserInPendo, initPendo, trackPendoEvent };
